import { ImgHTMLAttributes, useState } from 'react';
import { classes } from '../../utils/commonUtils';
import css from './image.module.scss';

interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  fallback?: string | undefined | (() => string | undefined);
  crop?: [x: number, y: number];
  preventDrag?: boolean;
  cors?: boolean;
}

const cache: Set<string> = new Set();
const FALLBACK = '/images/horizontal-placeholder.png';

export function Image({
  src,
  fallback = FALLBACK,
  alt = '',
  className,
  style,
  crop,
  preventDrag = false,
  cors,
  ...rest
}: ImageProps) {
  const [visible, setVisible] = useState(src ? cache.has(src) : false);
  const [isError, setIsError] = useState(false);

  function onError() {
    if (src) {
      cache.delete(src);
    }

    setIsError(true);
  }

  function onLoad() {
    if (src) {
      cache.add(src);
    }

    setVisible(true);
  }

  function getSrc() {
    if (isError) {
      return typeof fallback === 'function' ? fallback() || FALLBACK : fallback || FALLBACK;
    }

    if (crop) {
      return cropImage(src, crop);
    }

    return src;
  }

  return (
    <img
      onLoad={onLoad}
      onError={onError}
      crossOrigin={src?.includes('cdn') ? '' : undefined}
      className={classes(css.image, className)}
      style={style}
      src={getSrc()}
      alt={alt}
      draggable={!preventDrag}
      onMouseDown={(e) => preventDrag && e.preventDefault()}
      data-visible={visible}
      {...rest}
    />
  );
}

function cropImage(src: string | undefined, [x, y]: [x: number, y: number]) {
  return `${src}?x=${x}&y=${y}&ar=keep`;
}
